import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Flame, ArrowRight, ShoppingCart, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (price) => {
  if (!price) return "Op aanvraag";
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(price);
};

export default function PopularProducts({ onShopClick, onProductClick }) {
  const [products, setProducts] = useState([]);
  const scrollRef = useRef(null);
  const intervalRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products/popular?limit=12`)
      .then((r) => setProducts(r.data))
      .catch(() => {});
  }, []);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll);
    checkScroll();
    return () => el.removeEventListener("scroll", checkScroll);
  }, [products, checkScroll]);

  const scroll = useCallback((dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector("[data-card]")?.offsetWidth || 280;
    const gap = 16;
    const amount = (cardWidth + gap) * (dir === "left" ? -1 : 1);
    el.scrollBy({ left: amount, behavior: "smooth" });
  }, []);

  // Auto-scroll every 4 seconds
  useEffect(() => {
    if (products.length === 0 || isPaused) return;

    intervalRef.current = setInterval(() => {
      const el = scrollRef.current;
      if (!el) return;
      const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 10;
      if (atEnd) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        const cardWidth = el.querySelector("[data-card]")?.offsetWidth || 280;
        el.scrollBy({ left: cardWidth + 16, behavior: "smooth" });
      }
    }, 4000);

    return () => clearInterval(intervalRef.current);
  }, [products, isPaused]);

  if (products.length === 0) return null;

  return (
    <section data-testid="popular-products-section" className="py-20 px-4 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              <Flame size={22} className="text-[#FF3B30]" />
              <span className="text-[#FF3B30] text-xs font-bold uppercase tracking-[0.2em]">Trending</span>
            </div>
            <h2 className="text-white text-3xl sm:text-4xl font-bold font-['Orbitron'] leading-tight">
              Populaire <span className="text-[#FF3B30]">Producten</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {/* Nav arrows */}
            <div className="hidden sm:flex items-center gap-2">
              <button
                data-testid="popular-scroll-left"
                onClick={() => scroll("left")}
                disabled={!canScrollLeft}
                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                  canScrollLeft
                    ? "border-[#333] text-white hover:border-[#FF3B30] hover:text-[#FF3B30] bg-[#111]"
                    : "border-[#1E1E1E] text-[#333] cursor-not-allowed"
                }`}
              >
                <ChevronLeft size={18} />
              </button>
              <button
                data-testid="popular-scroll-right"
                onClick={() => scroll("right")}
                disabled={!canScrollRight}
                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                  canScrollRight
                    ? "border-[#333] text-white hover:border-[#FF3B30] hover:text-[#FF3B30] bg-[#111]"
                    : "border-[#1E1E1E] text-[#333] cursor-not-allowed"
                }`}
              >
                <ChevronRight size={18} />
              </button>
            </div>
            <button
              data-testid="popular-view-all"
              onClick={onShopClick}
              className="hidden sm:flex items-center gap-2 text-[#FF3B30] hover:text-white text-sm font-semibold transition-colors group"
            >
              Bekijk alles
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        {/* Scrollable product row */}
        <div
          ref={scrollRef}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => { setTimeout(() => setIsPaused(false), 5000); }}
          className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-2 -mx-4 px-4 snap-x snap-mandatory"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
        >
          {products.map((product, i) => (
            <div
              key={product.slug || i}
              data-card
              data-testid={`popular-product-${i}`}
              onClick={() => onProductClick?.(product)}
              className="group flex-shrink-0 w-[240px] sm:w-[260px] lg:w-[280px] bg-[#111] border border-[#1E1E1E] rounded-xl overflow-hidden hover:border-[#FF3B30]/40 transition-all duration-300 cursor-pointer hover:shadow-[0_0_30px_rgba(255,59,48,0.06)] snap-start"
            >
              <div className="aspect-square bg-white flex items-center justify-center overflow-hidden relative">
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt={product.title}
                    className="w-full h-full object-contain p-3 group-hover:scale-[1.04] transition-transform duration-500"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full bg-[#1a1a1a] flex items-center justify-center">
                    <span className="text-[#333] font-bold text-sm">{product.brand}</span>
                  </div>
                )}
                <span className="absolute top-2 left-2 text-[8px] font-bold uppercase tracking-widest bg-[#FF3B30] text-white px-2 py-0.5 rounded">
                  {product.brand}
                </span>
              </div>
              <div className="p-3.5">
                <h3 className="text-white text-[12px] sm:text-[13px] font-medium leading-snug line-clamp-2 min-h-[2.4rem] mb-2">
                  {product.title}
                </h3>
                <div className="flex items-center justify-between">
                  <span className="text-[#FF3B30] font-bold text-base">{formatPrice(product.price)}</span>
                  <ShoppingCart size={14} className="text-[#444] group-hover:text-[#FF3B30] transition-colors" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile CTA */}
        <button
          onClick={onShopClick}
          className="sm:hidden flex items-center justify-center gap-2 w-full mt-6 py-3 bg-[#111] border border-[#1E1E1E] rounded-xl text-[#FF3B30] font-semibold text-sm hover:border-[#FF3B30]/40 transition-all"
        >
          Bekijk alle producten <ArrowRight size={16} />
        </button>
      </div>
    </section>
  );
}
